import { AddTenantModal, DeleteConfirmModal, IconImage, RestrictUserModal, UnblockUserModal } from "@/components";
import { search_img, delete_img, unlock_right } from "@/constants";
import { useDebounce, useSearchQuery, useSnackbar } from "@/hooks";
import { useEnableDisableTenantMutation, useGetAllTenantsQuery, useLazyGetAccessSignedUrlQuery, useDeleteTenantMutation } from "@/services";
import {
  Avatar,
  Box,
  Button,
  Chip,
  InputAdornment,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, ReactElement, useState, useEffect } from "react";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import {
  HeaderRightNode,
  StyledContainedButton,
  StyledTextInput,
} from "./styles";
import { useGetTenantAllowedPagesQuery } from "@/graphql/page-access";
import { useGetSystemSettingsQuery, useSetIeepaRefundAlertMutation } from "@/graphql/system-settings";
import { tokens } from "@/tokens";
import { tableCompactSx } from "@/components/table/table-styles";
import { snakeToTitleCase } from "@/utils";

const defaultTenantDetails: ITenant = {
  _id: "",
  companyName: "",
  companyEmail: "",
  businessAddress: "",
  companyLogo: "",
  tenantType: "TRADEDIVE_CLIENT",
};

type ModalType = "delete" | "restrict" | "unblock" | "none";

interface ModalState {
  type: ModalType;
  open: boolean;
  tenantName: string;
  _id: string;
  email: string;
  isRestricted: boolean;
}

const initialModalState: ModalState = {
  type: "none",
  open: false,
  tenantName: "",
  _id: "",
  email: "",
  isRestricted: false,
};

const TDADMINTenantManagement: FC = (): ReactElement => {
  const [modalState, setModalState] = useState<ModalState>(initialModalState);
  const { updateQuery, getQueryValue } = useSearchQuery();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  const [tenantDetails, setTenantDetails] = useState<ITenant>(defaultTenantDetails);
  const [processedData, setProcessedData] = useState<ITenant[]>([]);
  const [getAccessSignedUrl, { isLoading: urlLoading }] = useLazyGetAccessSignedUrlQuery();

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllTenantsQuery(
    {
      pageSize: limit,
      pageNo: page + 1,
      searchKeyword: getQueryValue("search"),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [deleteTenant, { isLoading: deleteTenantLoading }] = useDeleteTenantMutation();
  const [enableDisableTenant, { isLoading: enableDisableTenantLoading }] = useEnableDisableTenantMutation();
  const { show: toast } = useSnackbar();
  const { data: systemSettings } = useGetSystemSettingsQuery();
  const [setIeepaRefundAlert, { isLoading: isTogglingIeepa }] = useSetIeepaRefundAlertMutation();
  const { data = [], meta } = apiData || {};
  const { countDocs: totalCount = 0 } = meta || {};
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedForPages, setSelectedForPages] = useState<ITenant | null>(null);
  useGetTenantAllowedPagesQuery(
    { tenantId: selectedForPages?._id },
    { skip: !selectedForPages?._id }
  );

  // Process tenant data to get signed URLs for company logos
  useEffect(() => {
    const fetchSignedUrls = async () => {
      if (!data.length) {
        setProcessedData([]);
        return;
      }
      try {
        const processedTenants = await Promise.all(
          data.map(async (tenant) => {
            if (tenant.companyLogo) {
              try {
                const { signedUrl } = await getAccessSignedUrl({
                  fileName: tenant.companyLogo,
                }).unwrap();
                return { ...tenant, companyLogo: signedUrl };
              } catch (error) {
                console.error("Error fetching signed URL:", error);
                return { ...tenant, companyLogo: "" };
              }
            }
            return { ...tenant, companyLogo: "" };
          })
        );
        setIsProcessing(false);
        setProcessedData(processedTenants);
      } catch (error) {
        setIsProcessing(false);
        console.error("Error processing tenants:", error);
      }
    };
    fetchSignedUrls();
  }, [data, getAccessSignedUrl]);

  const debouncedSearch = useDebounce((val: string) => {
    setPage(0);
    updateQuery({ search: val });
  }, 500);

  const handleRowClick = (tenant: ITenant) => {
    setTenantDetails(tenant);
    setShowAddTenantModal(true);
    setSelectedForPages(tenant);
  };

  const handleOpenAddClientModal = () => {
    setShowAddTenantModal(true);
    setTenantDetails(defaultTenantDetails);
    setSelectedForPages(null);
  };

  const handleTriggerModal = (item: ITenant, type: string) => {
    setModalState({
      type: type as ModalType,
      open: true,
      tenantName: item.companyName || "",
      _id: item._id || "",
      email: item.companyEmail || "",
      isRestricted: item.isRestricted || false,
    });
  };

  const handleCloseModal = () => {
    setModalState(initialModalState);
  };

  const handleUpdateTenantStatus = async () => {
    try {
      await enableDisableTenant({
        tenantId: modalState._id,
        isRestricted: !modalState.isRestricted,
      }).unwrap();
      handleCloseModal();
      setIsProcessing(true);
      toast("Tenant status updated successfully", "", "success");
    } catch (error: any) {
      console.error("Error updating tenant status:", error);
      toast(error?.message || "Failed to update tenant status", "", "error");
    }
  };

  const handleDeletePartner = async () => {
    try {
      await deleteTenant({
        tenantId: modalState._id,
      }).unwrap();
      handleCloseModal();
      setIsProcessing(true);
      toast("Tenant deleted successfully", "", "success");
    } catch (error: any) {
      console.error("Error deleting tenant:", error);
      toast(error?.message || "Failed to delete tenant", "", "error");
    }
  };

  const loading = isProcessing || isLoading || isFetching || urlLoading;

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" width={100} /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" width={150} /></TableCell>
      </TableRow>
    ));

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 3, pb: 1.5, flexShrink: 0 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Typography fontWeight={tokens.fontWeight.semibold} variant="h5" flex={1} color={tokens.text.primary}>
            Tenant Management
            {totalCount ? (
              <Box component="span" sx={{ color: tokens.text.muted, pl: 0.5 }}>
                ({totalCount})
              </Box>
            ) : null}
          </Typography>
          <HeaderRightNode gap={2}>
            <StyledTextInput
              type="text"
              placeholder="Search"
              handleChangeValue={debouncedSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconImage src={search_img} size="auto" />
                  </InputAdornment>
                ),
              }}
            />
            <StyledContainedButton
              startIcon={<IoMdAdd />}
              size="large"
              title="Add Tenant"
              onClick={handleOpenAddClientModal}
            />
          </HeaderRightNode>
        </Stack>
      </Box>

      {/* System-wide Feature Flags */}
      <Box
        sx={{
          mx: 2,
          mb: 1.5,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${systemSettings?.ieepaRefundAlert ? "#a5d6a7" : tokens.border.default}`,
          bgcolor: systemSettings?.ieepaRefundAlert ? "#f1f8e9" : tokens.bg.surface,
          transition: "all 0.2s ease",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={tokens.fontWeight.bold}>
                  IEEPA Refund Alert
                </Typography>
                <Tooltip
                  title="When enabled, all tenants will see a 'Refund opportunity' badge on IEEPA duty categories in the analytics dashboard, referencing the Supreme Court ruling."
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      fontSize: 10,
                      px: 0.75,
                      py: 0.15,
                      bgcolor: "rgba(0,0,0,0.06)",
                      borderRadius: 0.75,
                      cursor: "help",
                      color: tokens.text.secondary,
                      fontWeight: tokens.fontWeight.semibold,
                    }}
                  >
                    System-wide
                  </Box>
                </Tooltip>
              </Stack>
              <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                Show refund opportunity badge on IEEPA duties for all tenants
              </Typography>
            </Box>
          </Stack>
          <Switch
            checked={systemSettings?.ieepaRefundAlert ?? false}
            disabled={isTogglingIeepa}
            onChange={async (_, checked) => {
              try {
                await setIeepaRefundAlert({ enabled: checked }).unwrap();
                toast(
                  checked ? "IEEPA Refund Alert enabled" : "IEEPA Refund Alert disabled",
                  "",
                  "success"
                );
              } catch (error: any) {
                toast(error?.message || "Failed to update setting", "", "error");
              }
            }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#2e7d32",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#66bb6a",
              },
            }}
          />
        </Stack>
      </Box>

      {/* Table */}
      <Box sx={{ flex: 1, overflow: "hidden", px: 2, pb: 2 }}>
        <Box sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          ...tableCompactSx,
        }}>
          <TableContainer sx={{ flex: 1, overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Tenant Name</TableCell>
                  <TableCell>Company Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Client IDs</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? renderSkeletonRows() : (
                  <>
                    {processedData.map((item) => {
                      const spClients = ((item as any).users ?? []).filter(
                        (u: { userRole?: string }) => u.userRole === "SP_CLIENT"
                      );
                      return (
                      <TableRow
                        key={item._id}
                        onClick={() => handleRowClick(item)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar
                              src={item.companyLogo || undefined}
                              sx={{ width: 24, height: 24, fontSize: 11, fontWeight: tokens.fontWeight.bold, bgcolor: tokens.bg.muted, color: tokens.text.muted, border: `1px solid ${tokens.border.light}` }}
                            >
                              {item.companyName?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <Typography sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, fontSize: tokens.fontSize.sm2 }}>
                              {item.companyName || "Unnamed"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {item.companyEmail || "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={snakeToTitleCase(item.tenantType)}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: tokens.fontSize["2xs"],
                              fontWeight: tokens.fontWeight.semibold,
                              borderRadius: tokens.radius.full,
                              ...(item.tenantType === "SERVICE_PROVIDER"
                                ? { bgcolor: tokens.chip.indigo.bg, color: tokens.chip.indigo.text }
                                : { bgcolor: tokens.chip.green.bg, color: tokens.chip.green.text }),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {item.tenantType === "TRADEDIVE_CLIENT" ? (
                            <Typography sx={{ fontFamily: "monospace", fontSize: tokens.fontSize.xs, color: tokens.text.secondary }}>
                              {item._id}
                            </Typography>
                          ) : spClients.length > 0 ? (
                            <Stack spacing={0.25}>
                              {spClients.map((u: { _id: string }) => (
                                <Typography key={u._id} sx={{ fontFamily: "monospace", fontSize: tokens.fontSize.xs, color: tokens.text.secondary }}>
                                  {u._id}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.xs }}>N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {!item.isRestricted ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<IoMdClose />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTriggerModal(item, "restrict");
                                }}
                                sx={{
                                  color: tokens.status.danger,
                                  borderColor: tokens.border.default,
                                  fontWeight: tokens.fontWeight.semibold,
                                  fontSize: tokens.fontSize.xs,
                                  textTransform: "none",
                                }}
                              >
                                Restrict
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<IconImage src={unlock_right} size="auto" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTriggerModal(item, "unblock");
                                }}
                                sx={{
                                  color: tokens.status.success,
                                  borderColor: tokens.border.default,
                                  fontWeight: tokens.fontWeight.semibold,
                                  fontSize: tokens.fontSize.xs,
                                  textTransform: "none",
                                }}
                              >
                                Unblock
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTriggerModal(item, "delete");
                              }}
                              sx={{
                                color: tokens.status.danger,
                                borderColor: tokens.border.default,
                                minWidth: "auto",
                                px: 1,
                              }}
                            >
                              <IconImage src={delete_img} size="auto" />
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {processedData.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                            No tenants found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalCount > 0 && (
            <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, flexShrink: 0 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  ".MuiTablePagination-toolbar": { minHeight: 40 },
                  ".MuiTablePagination-displayedRows": { fontSize: tokens.fontSize.base, color: tokens.text.secondary },
                  ".MuiTablePagination-selectLabel": { fontSize: tokens.fontSize.base, color: tokens.text.secondary },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Add/Edit Tenant Modal */}
      {showAddTenantModal && (
        <AddTenantModal
          show={showAddTenantModal}
          setShow={setShowAddTenantModal}
          selectedTenant={tenantDetails}
        />
      )}

      {/* Delete confirmation Modal */}
      {modalState.type === "delete" && (
        <DeleteConfirmModal
          open={modalState.open}
          itemName={`Tenant "${modalState.tenantName}"`}
          subText="tenant"
          onConfirm={handleDeletePartner}
          maxWidth="xs"
          onClose={handleCloseModal}
          btnLoader={deleteTenantLoading}
        />
      )}

      {modalState.type === "restrict" && (
        <UnblockUserModal
          open={modalState.open}
          name={modalState.tenantName}
          onConfirm={handleUpdateTenantStatus}
          onClose={handleCloseModal}
          loading={enableDisableTenantLoading}
        />
      )}

      {modalState.type === "unblock" && (
        <RestrictUserModal
          open={modalState.open}
          name={modalState.tenantName}
          onConfirm={handleUpdateTenantStatus}
          onClose={handleCloseModal}
          loading={enableDisableTenantLoading}
          description="tenant"
        />
      )}
    </Stack>
  );
};

export default TDADMINTenantManagement;
